/**
 * SlidePanelActions — standard Cancel + primary-action footer row.
 *
 * Encapsulates the repeated button/tooltip/loader pattern so callers that use
 * the bare `SlidePanel` component don't have to inline boilerplate every time.
 *
 * Usage inside a SlidePanel's `footerActions` prop:
 *
 *   <SlidePanel
 *     open={open}
 *     onClose={onClose}
 *     title="My Panel"
 *     footerActions={
 *       <SlidePanelActions
 *         onCancel={onClose}
 *         onSave={e => formRef?.onSubmit(e as unknown as React.SyntheticEvent<HTMLFormElement>)}
 *         saveText="Deploy"
 *         saveDisabled={isDisabled}
 *         isSubmitting={isDeploying}
 *       />
 *     }
 *   >
 *     ...
 *   </SlidePanel>
 *
 * FormSlidePanel uses this internally — you only need it when working directly
 * with the bare SlidePanel shell.
 */
import { Button } from "@progress/kendo-react-buttons";
import React from "react";
import { useTranslation } from "react-i18next";
import ToolTip from "@/components/Kendo/ToolTip";
import { SlidePanelActionsProps } from "./types";
import "./panel-footer-actions.scss";

const SlidePanelActions: React.FC<SlidePanelActionsProps> = ({
  onCancel,
  cancelText,
  cancelDisabled,
  onSave,
  saveText,
  saveDisabled = false,
  saveTooltip = "",
  isFreemium,
  isSubmitting = false,
  saveButtonTheme = "secondary",
  saveButtonId,
  leftActions,
}) => {
  const { t } = useTranslation();
  const resolvedCancelText = cancelText ?? t("slidePanel.cancel");
  const resolvedSaveText = saveText ?? t("slidePanel.save");

  return (
    <>
      <div className="tlx-panel-footer__actions-left">{leftActions}</div>
      <div className="tlx-panel-footer__actions-right">
        <Button
          type="button"
          onClick={onCancel}
          disabled={cancelDisabled ?? isSubmitting}
          size="medium"
        >
          {resolvedCancelText}
        </Button>

        <ToolTip
          anchorElement="target"
          title={saveTooltip}
          isFreemium={saveTooltip ? isFreemium : undefined}
        >
          <Button
            type="button"
            id={saveButtonId}
            themeColor={saveButtonTheme}
            disabled={saveDisabled || isSubmitting}
            onClick={onSave}
            icon={isSubmitting ? "loading" : undefined}
            size="medium"
          >
            {resolvedSaveText}
          </Button>
        </ToolTip>
      </div>
    </>
  );
};

export default SlidePanelActions;
